import React from 'react';
import dayjs from 'dayjs';
import Picture from './Picture';
import { getAdminBadge } from '../utils/adminBadge';
import { can } from '../utils/can.js';

const clientColors = [
  'from-red-500 to-rose-500',
  'from-blue-500 to-sky-500',
  'from-green-500 to-emerald-500',
  'from-yellow-500 to-amber-500',
  'from-purple-500 to-violet-500',
  'from-pink-500 to-fuchsia-500',
  'from-indigo-500 to-blue-500',
  'from-orange-500 to-amber-500',
  'from-teal-500 to-cyan-500',
  'from-cyan-500 to-blue-500'
];

const getClientColor = (client = '') => {
  const index = client.charAt(0).toLowerCase().charCodeAt(0) % clientColors.length;
  return clientColors[index] || clientColors[0];
};

function GalleryPictureCard({
  picture,
  currentUser,
  isAdmin,
  isSelected,
  obfuscate,
  gridSize,
  onOpen,
  onToggleSelection,
  t
}) {
  const imageHeightClass = [
    gridSize === 'small' ? 'h-48 sm:h-56 md:h-40 lg:h-40' : '',
    gridSize === 'medium' ? 'h-48 sm:h-56' : '',
    gridSize === 'large' ? 'h-48 sm:h-56 md:h-80 lg:h-96' : ''
  ].filter(Boolean).join(' ');
  const iconSize = gridSize === 'large' ? 'h-8 w-8' : 'h-7 w-7';

  return (
    <article
      className={[
        'group relative overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-300 dark:bg-gray-900 cursor-pointer',
        isSelected
          ? 'border-blue-500 ring-2 ring-blue-500/30 shadow-blue-500/10'
          : 'border-gray-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-xl dark:border-gray-800 dark:hover:border-gray-700'
      ].join(' ')}
      onClick={onOpen}
    >
      <div className="absolute inset-x-3 top-3 z-10 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col items-start gap-1">
          {picture.admin_only && (
            <div className="inline-flex max-w-full items-center gap-1 rounded-full border border-purple-300/40 bg-purple-950/75 px-2.5 py-1 text-xs font-medium text-white shadow-lg backdrop-blur">
              {getAdminBadge()}
              <span className="truncate">{t('Not visible for the client')}</span>
            </div>
          )}

          {currentUser?.multiclient && (
            <div className={`max-w-full truncate rounded-full bg-gradient-to-r ${getClientColor(picture.client)} px-2.5 py-1 text-xs font-semibold text-white shadow-lg`}>
              {obfuscate ? '********' : picture.client}
            </div>
          )}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {picture.has_attachments && (
            <div
              className={`flex ${iconSize} items-center justify-center rounded-full border border-white/40 bg-black/55 text-white shadow-lg backdrop-blur`}
              title={t('Several photos of this product')}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 8V5a1 1 0 011-1h10a1 1 0 011 1v10a1 1 0 01-1 1h-3M5 8h10a1 1 0 011 1v10a1 1 0 01-1 1H5a1 1 0 01-1-1V9a1 1 0 011-1z" />
              </svg>
              <span className="sr-only">{t('Several photos of this product')}</span>
            </div>
          )}

          {isAdmin && picture.ai_agents_count > 0 && (
            <div
              className={`flex ${iconSize} items-center justify-center rounded-full border border-white/40 bg-black/55 text-sky-300 shadow-lg backdrop-blur`}
              title={picture.ai_agents_count === 1
                ? t('Analyzed by one AI assistant')
                : t('Analyzed by {{count}} AI assistants', { count: picture.ai_agents_count })}
            >
              <span aria-hidden="true" className="text-sm leading-none">✦</span>
              <span className="sr-only">{t('Analyzed by one AI assistant')}</span>
            </div>
          )}

          {(picture.favorite || picture.deletedat) && (
            <div className="flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-white shadow-lg backdrop-blur">
              {picture.favorite && (
                <svg className="h-4 w-4 text-red-400 fill-current" viewBox="0 0 24 24">
                  <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              )}
              {picture.deletedat && (
                <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              )}
            </div>
          )}

          {can(currentUser, 'pictures.export') && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelection();
              }}
              className={[
                `flex ${iconSize} items-center justify-center rounded-full border shadow-lg transition-all backdrop-blur`,
                isSelected
                  ? 'border-blue-300 bg-blue-600 text-white'
                  : 'border-white/40 bg-black/45 text-white hover:bg-black/65'
              ].join(' ')}
              title={isSelected ? t('Remove from export cart') : t('Add to export cart')}
            >
              {isSelected ? (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l3-8H5.4M7 13L5.4 5M7 13l-2 6h14M9 19a1 1 0 11-2 0 1 1 0 012 0zm10 0a1 1 0 11-2 0 1 1 0 012 0z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      <Picture
        pictureurl={picture.pictureurl}
        className={[
          'w-full object-cover transition duration-300 group-hover:scale-[1.03]',
          imageHeightClass,
          obfuscate ? 'blur-sm' : ''
        ].filter(Boolean).join(' ')}
        lazy={true}
      />

      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent p-3 pt-12 text-white">
        <div className="space-y-1">
          <h3 className="truncate text-sm font-semibold">{picture.shopname}</h3>
          {/* Gated on the caller's rights, not only on the field being there: the server answers a
              real administrator, so the origin still arrives while previewing the client's view. */}
          {(picture.product_via_item || picture.product_via_ai) && (
            <p
              className="flex items-center gap-1 truncate whitespace-nowrap text-xs font-semibold text-white"
              title={t('How this photo matched the product')}
            >
              {picture.product_via_item && <span>{t('Declared')}</span>}
              {picture.product_via_item && picture.product_via_ai && <span aria-hidden="true">+</span>}
              {picture.product_via_ai && <span className="truncate text-sky-300">{t('Seen by AI')}</span>}
            </p>
          )}
          {picture.sector && <p className="truncate text-xs text-white/80">{picture.sector}</p>}
          <p className="text-xs text-white/70">{dayjs(picture.createdat).format('L')}</p>
        </div>

        <div className="max-h-0 overflow-hidden transition-all duration-300 group-hover:max-h-32">
          <div className="mt-3 space-y-1 border-t border-white/15 pt-3 text-xs text-white/85">
            {picture.projectname && <p><span className="font-semibold">{t('Project')}:</span> {picture.projectname.includes('-') ? picture.projectname.split(' ').slice(1).join(' ').trim().replace(/^-/, '').trim() : picture.projectname}</p>}
            {picture.question && <p><span className="font-semibold">{t('Question')}:</span> {picture.translatedquestion}</p>}
            {picture.item && <p><span className="font-semibold">{t('Item')}:</span> {picture.item}</p>}
          </div>
        </div>
      </div>
    </article>
  );
}

export default GalleryPictureCard;
